import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getPayments, createPayment, deletePayment, getOrders } from '@/lib/api';
import { Plus, Search, Trash2, CreditCard } from 'lucide-react';
import { format } from 'date-fns';

const paymentMethods = ['Cash', 'Credit Card', 'Debit Card', 'Bank Transfer', 'Check', 'Other'];

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    order_id: '',
    amount: '',
    payment_method: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, ordersRes] = await Promise.all([
        getPayments(),
        getOrders(),
      ]);
      setPayments(paymentsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createPayment({
        ...formData,
        amount: parseFloat(formData.amount),
      });
      toast.success('Payment recorded successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await deletePayment(paymentId);
      toast.success('Payment deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const resetForm = () => {
    setFormData({
      order_id: '',
      amount: '',
      payment_method: '',
      notes: '',
    });
  };

  const filteredPayments = payments.filter((payment) =>
    payment.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.payment_method.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="space-y-8 p-3" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Payments
          </h1>
          <p className="text-muted-foreground">Track and record payments</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow"
              data-testid="add-payment-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                Record Payment
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-uppercase">Order *</Label>
                <Select
                  value={formData.order_id}
                  onValueChange={(value) => setFormData({ ...formData, order_id: value })}
                  required
                >
                  <SelectTrigger data-testid="payment-order-select" className="h-11 bg-secondary/50">
                    <SelectValue placeholder="Select order" />
                  </SelectTrigger>
                  <SelectContent>
                    {orders.map((order) => (
                      <SelectItem key={order.order_id} value={order.order_id}>
                        {order.order_number} - {order.customer_name} ({formatCurrency(order.order_value)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Amount (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="payment-amount-input"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="h-11 bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Payment Method *</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  required
                >
                  <SelectTrigger data-testid="payment-method-select" className="h-11 bg-secondary/50">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((method) => (
                      <SelectItem key={method} value={method}>{method}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Notes</Label>
                <Textarea
                  data-testid="payment-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Payment notes..."
                  className="bg-secondary/50 min-h-[80px]"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-payment-button"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Record Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <Card className="border-white/10 bg-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="label-uppercase">Total Payments Received</p>
              <p className="text-3xl font-semibold text-emerald-400 mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                {formatCurrency(totalPayments)}
              </p>
            </div>
            <div className="flex items-center justify-center w-14 h-14 rounded-sm bg-emerald-500/10 text-emerald-400">
              <CreditCard className="w-7 h-7" strokeWidth={1.5} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="payment-search-input"
          placeholder="Search payments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-11 bg-secondary/50"
        />
      </div>

      {/* Table */}
      <Card className="border-white/10 bg-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CreditCard className="w-12 h-12 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-1">No payments found</h3>
              <p className="text-muted-foreground text-sm">
                {searchTerm ? 'Try a different search term' : 'Record your first payment to get started'}
              </p>
            </div>
          ) : (
            <Table className="table-luxury">
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment) => (
                  <TableRow key={payment.payment_id} className="table-row-hover">
                    <TableCell className="font-mono text-sm">{payment.order_number || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(payment.payment_date), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="font-medium text-emerald-400">
                      {formatCurrency(payment.amount)}
                    </TableCell>
                    <TableCell>{payment.payment_method}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {payment.notes || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(payment.payment_id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-payment-${payment.payment_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;
